import React, { useState, useEffect, lazy, Suspense, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { Snackbar, Alert, Slide } from "@mui/material";

/***** ` Utils....` *****/
import { actions } from "./redux/store/store";
import { useGetCurrentUserQuery } from "./api/auth";

/***** ` Modals....` *****/
import DriverModal from "../src/components/shared/customModal/driver";
import StoreModal from "../src/components/shared/customModal/store";
import TransactionsModal from "../src/components/shared/customModal/transactions";
import ReasonModal from "../src/components/shared/customModal/reason";
import OrderModal from "../src/components/shared/customModal/order";
import ShiftTemplateModal from "../src/components/shared/customModal/shiftTemplate";
import AdhocPaymentsModal from "../src/components/shared/customModal/adhocPayment";
import DirectPaymentsModal from "../src/components/shared/customModal/directPayment";
import MetroModal from "./components/shared/customModal/metros";
import NotificationModal from "./components/shared/customModal/notification";
import NotesModal from "./components/shared/customModal/notesModal";
import EmailModal from "./components/shared/customModal/email";
import OrderLogsDetailsModal from "./components/shared/orderHelper/OrderLogs";
import ShiftPage from "./views/pages/Shift-Page";
import CompanyMainModal from "./components/shared/customModal/company";
import CoachingModal from "./components/shared/customModal/coaching";

/***** `Components....` *****/
import AuthHandler from "../src/auth/AuthHandler";
import Menu from "../src/components/common/Menu";
import Header from "../src/components/common/Header";
// import Topbar from "../src/components/shared/topbar";
// import DirectPayment from "./components/DirectPayment";

/***** ` Styles....` *****/
import "../src/styles/app.scss";

/***** ` Pages....` *****/
import AdhocPayment from "./views/pages/AdhocPayment";
import Confirmation from "../src/components/Confirmation";
import Drivers from "../src/views/pages/Drivers";
import Deposit from "./views/pages/Deposit";
import DriversEarning from "./views/pages/Drivers-Earning";
import Email from "./views/pages/Email";
import ForgotPassword from "./views/pages/ForgotPassword";
import Login from "./views/pages/Login";
import LinksToDocs from "../src/views/pages/LinksToDocs";
import Metros from "./views/pages/Metros";
import Notifications from "./views/pages/Notifications";
import Orders from "./views/pages/Orders";
import Payments from "../src/components/Payments";
import Register from "./views/pages/Register";
import ResetPassword from "../src/components/ResetPassword";
import ShiftTemplate from "../src/components/ShiftTemplate";
import Stores from "../src/components/Stores";
import Scheduling from "./views/pages/Scheduling";
import Transactions from "./views/pages/Transactions-Report";
import Users from "../src/components/Users";
import { MuiLoader } from "./components/common/loading/Loader";
import DriverReport from "./views/pages/DriverReport";
import PiplineStage from "./views/pages/Pipline-Stage";
import Operator from "./views/pages/Operator";
import CompanyModal from "./components/shared/customModal/companyModal";
import Company from "./views/pages/Company";
import Feedback from "./views/pages/Feedback";
import PhoneModal from "./components/shared/customModal/phoneModal";
import ScheduleModal from "./components/shared/customModal/scheduleModal";
import DriverCoach from "./views/pages/DriverCoach";
import CoachingHistoryModal from "./components/shared/customModal/coaching/CoachingHistory";
import AddExceptionModal from "./components/shared/customModal/order/AddExceptionModal";
import Ratings from "./components/shared/customModal/coaching/Ratings";
import PaymentCalendar from "./views/pages/PaymentCalendar";
import PaymentCalendarModel from "./components/shared/customModal/paymentCalendar";
import InterestedDriverModal from "./components/shared/customModal/order/InterestedDriverModal"
import PODsModal from "./components/shared/orderHelper/PODs";
import BulkOrderModal from "./components/shared/orderHelper/BulkOrder";
import BulkZoneModal from "./components/shared/orderHelper/BulkZone";
import DriverLicenseModal from "./components/shared/customModal/driverLicenseModal";
import InsurancePolicyModal from "./components/shared/customModal/insurancePolicyModal";
import { HealthCheck } from "./views/pages/HealthCheck";
import VehicleModal from "./components/shared/customModal/vehicle";
import OAIModal from "./components/shared/customModal/oaiModal";
import { DriverLogsModal } from "./components/shared/customModal/driver/driverLogs";
import BonusCampaign from "./views/pages/BonusCampaign";
import BonusDisbursement from "./views/pages/BonusCampaign/Disbursement";
import BonusCampaignModel from "./components/shared/customModal/bonusCampaign";
import BonusReport from "./views/pages/BonusCampaign/Report";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import authConfig from "./authConfig";
import Callback from "./views/pages/Callback";
import ProspectDrivers from "./views/pages/ProspectDrivers";
import ProgressWindow from "./views/pages/ProspectDrivers/ProgressWindow";
import { useVehicleType } from "./constants/hooks/useVehicleType";
import { DriverBulkUpdate } from "./components/shared/customModal/driverBulkUpdate";

// import Topbar from "../src/components/shared/topbar";
// import DirectPayment from "./components/DirectPayment";
const Pipeline = lazy(() => import("./views/pages/Pipeline"));
const PipelineModal = lazy(() =>
  import("./components/shared/customModal/pipeline")
);
const SlideUp = (p) => <Slide {...p} direction="up" />;

const routes = ['/healthCheck', '/resetPassword/:hash', '/register/:hash', '/confirmation/:hash'];

const shouldSkipUserQuery = routes.some((route) => {
  if (route.includes(':')) {
    const regex = new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}$`);
    return regex.test(window.location.pathname);
  }
  return route === window.location.pathname;
});

function App() {
  const {
    driver,
    order,
    shifttemplate,
    store,
    transaction,
    reason, adhocpayment,
    directpayment,
    metro,
    campaign,
    calendarPayment,
    orderLogs,
    oaiLogs,
    email,
    notification,
    notes,
    phone,
    driverLicense,
    insurancePolicy,
    pipeline,
    company,
    companymain,
    schedule,
    vehicle,
    coaching,
    coachingHistory,
    addException,
    rating,
    interestedDriver,
    pods,
    bulkOrder,
    bulkZone,
    driverBulkUpdate,
    oai_insurance
  } = useSelector((state) => state.modal);

  const currentUser = useSelector((state) => state.auth.currentUser)
  const isLoading = useSelector((state) => state.auth.isLoading)

  const [usersTab, setUsersTab] = useState("Current");
  const [scTab, setScTab] = useState("pipeline_drivers");
  const [stopUserQuery, setStopUserQuery] = useState(false);
  const [fetchUserState, setFetchUserState] = useState(true);

  const currentUserQuery = useGetCurrentUserQuery(null, {
    skip: shouldSkipUserQuery || !fetchUserState,
  });

  useEffect(() => {
    if (!stopUserQuery) {
      actions.auth.setCurrentUser(currentUserQuery.data || null);
      actions.auth.setLoading(currentUserQuery.isLoading);
    }
  }, [currentUserQuery, stopUserQuery]);

  const notifications = useSelector(
    (state) => state.notifications.notifications
  );

  const { isDevMode, AuthConfig } = useMemo(() => {
    const origin = window.location.origin;
    return {
      isDevMode: window.location.hostname.includes("dev"),
      AuthConfig: {
        ...authConfig,
        redirect_uri: `${origin}/callback`,
        post_logout_redirect_uri: origin
      }
    }
  }, []);

  const userManager = new UserManager({
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    ...AuthConfig,
  });

  const handleLogin = (e) => {
    e.preventDefault();
    sessionStorage.setItem("isLoggingIn", "true");
    userManager.signinRedirect();
  };

  useEffect(() => {
    if (sessionStorage.getItem("isLoggingIn") == "true") {
      actions.auth.setLoading(true);
    }
  }, []);

  useVehicleType();

  return (
      <>
          {isLoading && <MuiLoader />}
          <Router>
              <Suspense fallback={<MuiLoader />}>
                  <AuthHandler />
                  {!stopUserQuery && currentUser && (
                      <>
                          <Header
                              userManager={userManager}
                              isDevMode={isDevMode}
                          />
                          <Menu isDevMode={isDevMode} />
                          <ProgressWindow />
                      </>
                  )}
                  <Routes>
                      {!stopUserQuery && !isLoading && (
                          <>
                              <Route
                                  path="/healthCheck"
                                  element={<HealthCheck />}
                              ></Route>
                              <Route
                                  path="/login"
                                  element={
                                      <Login
                                          onStopUserQuery={setStopUserQuery}
                                          handleLogin={handleLogin}
                                          setFetchUserState={setFetchUserState}
                                      />
                                  }
                              />
                              <Route
                                  path="/callback"
                                  element={
                                      <Callback
                                          onStopUserQuery={setStopUserQuery}
                                          userManager={userManager}
                                      />
                                  }
                              />
                              <Route
                                  path="/register/:hash"
                                  element={
                                      <Register
                                          onStopUserQuery={setStopUserQuery}
                                      />
                                  }
                              />
                              <Route
                                  path="/confirmation/:hash"
                                  element={<Confirmation />}
                              />
                              <Route
                                  path="/forgotPassword"
                                  element={<ForgotPassword />}
                              />
                              <Route
                                  path="/resetPassword/:hash"
                                  element={
                                      <ResetPassword
                                          onStopUserQuery={setStopUserQuery}
                                      />
                                  }
                              />
                          </>
                      )}

                      {!stopUserQuery && !isLoading && currentUser && (
                          <>
                              <Route
                                  path="/drivers"
                                  element={<Drivers />}
                              ></Route>
                              <Route
                                  path="/prospectDrivers"
                                  element={<ProspectDrivers />}
                              ></Route>
                              <Route
                                  path="/shifts"
                                  element={<Orders />}
                              ></Route>
                              <Route
                                  path="/shiftstemplate"
                                  element={<ShiftTemplate />}
                              ></Route>
                              <Route path="/hubs" element={<Stores />}></Route>
                              <Route
                                  path="/adhocpayment"
                                  element={<AdhocPayment />}
                              ></Route>
                              {/* <Route path="/directpayment" element={<DirectPayment />}></Route> */}
                              <Route
                                  path="/shiftpage"
                                  element={<ShiftPage />}
                              ></Route>
                              {/* <Route path="/driversreport" element={<DriverReport />}></Route> */}
                              <Route
                                  path="/piplinestage"
                                  element={<PiplineStage />}
                              ></Route>
                              <Route
                                  path="/operator"
                                  element={<Operator />}
                              ></Route>
                              <Route
                                  path="/feedback"
                                  element={<Feedback />}
                              ></Route>
                              <Route
                                  path="/drivercoaching"
                                  element={<DriverCoach />}
                              ></Route>
                              <Route
                                  path="/bonusCampaign"
                                  element={<BonusCampaign />}
                              />
                              <Route
                                  path="/bonusCampaign/disbursement"
                                  element={<BonusDisbursement />}
                              />
                              <Route
                                  path="/bonusCampaign/report"
                                  element={<BonusReport />}
                              />

                              <Route
                                  path="/users/:tab"
                                  element={
                                      <Users
                                          onSetUsersTab={setUsersTab}
                                          usersTab={usersTab}
                                      />
                                  }
                              />
                              <Route
                                  path="/users"
                                  element={
                                      <Users
                                          onSetUsersTab={setUsersTab}
                                          usersTab={usersTab}
                                      />
                                  }
                              />
                              {/* <Route path="/links-to-docs" element={<LinksToDocs />}></Route> */}
                              <Route path="/reports" />
                              <Route
                                  path="/reports/drivers-earning"
                                  element={<DriversEarning />}
                              />
                              <Route
                                  path="/reports/transactions"
                                  element={<Transactions />}
                              />
                              <Route
                                  path="/deposit"
                                  element={<Deposit />}
                              ></Route>
                              <Route path="/payments" element={<Payments />} />
                              <Route
                                  path="/paymentsCalendar"
                                  element={<PaymentCalendar />}
                              />
                              <Route path="/metros" element={<Metros />} />
                              <Route path="/emails" element={<Email />} />
                              <Route
                                  path="/scheduling"
                                  element={<Scheduling />}
                              />
                              <Route
                                  path="/notifications"
                                  element={<Notifications />}
                              />
                              <Route path="/shifts" element={<Orders />} />
                              <Route
                                  path="/schedulers/:type"
                                  element={
                                      <Pipeline
                                          onSetScTab={setScTab}
                                          usersTab={scTab}
                                      />
                                  }
                              />
                              <Route
                                  path="/schedulers"
                                  element={
                                      <Pipeline
                                          onSetScTab={setScTab}
                                          usersTab={scTab}
                                      />
                                  }
                              />

                              <Route
                                  path="*"
                                  element={<Navigate to="/hubs" />}
                              />
                              <Route
                                  path="/company"
                                  element={<Company />}
                              ></Route>
                          </>
                      )}
                  </Routes>
              </Suspense>
          </Router>
          <Suspense fallback={<MuiLoader />}>
              {driver.open && <DriverModal id={driver.id} />}
              {order.open && <OrderModal id={order.id} />}
              {shifttemplate.open && (
                  <ShiftTemplateModal id={shifttemplate.id} />
              )}
              {store.open && <StoreModal id={store.id} />}
              {transaction.open && <TransactionsModal id={transaction.id} />}
              {reason.open && <ReasonModal id={reason.id} />}
              {adhocpayment.open && <AdhocPaymentsModal id={order.id} />}
              {directpayment.open && (
                  <DirectPaymentsModal id={directpayment.id} />
              )}
              {metro.open && <MetroModal id={metro.id} />}
              {campaign.open && <BonusCampaignModel id={campaign.id} />}
              {calendarPayment.open && <PaymentCalendarModel id={metro.id} />}
              {orderLogs.open && <OrderLogsDetailsModal id={orderLogs.id} />}
              {oaiLogs.open && <DriverLogsModal id={oaiLogs.id} />}
              {email.open && <EmailModal id={email.id} />}
              {notification.open && <NotificationModal id={notification.id} />}
              {notes.open && <NotesModal id={notes.id} />}
              {phone.open && <PhoneModal id={phone.id} />}
              {driverLicense.open && (
                  <DriverLicenseModal id={driverLicense.id} />
              )}
              {insurancePolicy.open && (
                  <InsurancePolicyModal id={insurancePolicy.id} />
              )}
              {oai_insurance.open && <OAIModal id={oai_insurance.id} />}
              {pipeline.open && <PipelineModal id={pipeline.id} />}
              {company.open && <CompanyModal id={company.id} />}
              {companymain.open && <CompanyMainModal id={companymain.id} />}
              {schedule.open && <ScheduleModal id={schedule.id} />}
              {vehicle.open && <VehicleModal id={vehicle.id} />}
              {coaching.open && <CoachingModal id={coaching.id} />}
              {coachingHistory.open && (
                  <CoachingHistoryModal id={coachingHistory.id} />
              )}
              {addException.open && <AddExceptionModal id={addException.id} />}
              {rating.open && <Ratings id={rating.id} />}
              {pods.open && <PODsModal id={pods.id} />}
              {bulkOrder.open && <BulkOrderModal id={bulkOrder.id} />}
              {driverBulkUpdate.open && <DriverBulkUpdate id={driverBulkUpdate.id}/>}
        {bulkZone.open && <BulkZoneModal id={bulkZone.id} />}

              {interestedDriver.open && (
                  <InterestedDriverModal id={interestedDriver.id} />
              )}
          </Suspense>
          {notifications.map(({ message, open, status }) => (
              <Snackbar
                  key={message}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  open={open}
                  autoHideDuration={3000}
                  onClose={() => actions.notifications.close(message)}
                  TransitionComponent={SlideUp}
              >
                  <Alert severity={status}>{message}</Alert>
              </Snackbar>
          ))}
      </>
  );
}

export default App;
